/**
 * OMON-035 draft persistence — Firestore-backed save/restore/discard for
 * the planner's in-flight draft, plus the publish-as-live-version flow.
 *
 * Drafts are a single `_carePlan` doc per (EoC, planner) with
 * `status: "draft"` holding the builder state as a serialised JSON blob
 * in `_draftState`. Drafts don't materialise `_goal` docs until promoted
 * to active — keeps autosave write volume to ONE doc per debounce tick
 * rather than N+1.
 *
 * `savePlan`, `loadPatientPlan`, `renderClientsView` are imported from
 * `../main` (circular import; ES modules resolve fine because all four
 * are used inside function bodies, not at top-level).
 *
 * Extracted from main.ts as part of OMON-060 Phase 6b.3.
 *
 * KNOWN BUG (filed as OMON-064): restoreDraftState parses JSON into a
 * local variable named `state` which shadows the imported state module.
 * All writes inside the function target the parsed object, not the app
 * state — resume-draft is silently broken. Preserved verbatim here so
 * this PR stays a pure code move; fixed separately.
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";

import { state } from "../state";
import { db } from "../firebase";
import { showToast } from "./list";
import { savePlan } from "./plan-builder";
import { loadPatientPlan } from "../main";
import { renderClientsView } from "./view";
import { canPublish, isEmptyPlan, shouldSaveDraft } from "./publish-rules";
import { escapeHtml } from "../utils/html";
import { draftCustodianOf, toDraftCustodian } from "../loaders/plan-stewardship";
import { normaliseRoles } from "./shift-role-policy";
import { ensureMinimumSet, readAllowNotApplicable } from "./monitored";
// OMON-143 (2026-05-24): stamp meta.security[HTEST] on draft _carePlan
// docs created by `dataScope: test` users.
import { metaSecurityFor } from "../admin/meta-security";

// OMON-035: Firestore-backed draft persistence. Drafts are a single
// `_carePlan` doc per (EoC, planner) with `status: "draft"` holding the
// builder state as a serialised JSON blob in `_draftState`. Drafts don't
// materialise `_goal` docs until promoted to active — keeps autosave write
// volume to ONE doc per debounce tick rather than N+1.
export async function saveDraft() {
  if (!state.selectedPatient) return;
  // Save what is on screen, an EMPTIED draft included (Ed, 2026-09-24): the
  // old "nothing on it → don't save" guard left the last deleted item in the
  // stored draft, so it came back on the next visit. See publish-rules.ts.
  const empty = isEmptyPlan(state.builderGoals.length, state.builderMonitoredEvents.length);
  if (!shouldSaveDraft(empty, state.currentDraftId !== null, state.patientCarePlans.length > 0)) return;
  // After the empty check (an emptied draft stays saveable), before the write:
  // no stored draft lacks the minimum set (Ed 2026-09-29).
  state.builderMonitoredEvents = ensureMinimumSet(state.builderMonitoredEvents, state.catalogueData);
  const eocId = state.selectedPatient.docId;
  const draftStateJson = JSON.stringify({
    builderGoals: state.builderGoals.map((o) => ({
      ...o,
      _overrides: Array.from(o._overrides),
    })),
    // OMON-119 FULL: monitored events serialised alongside goals so a
    // resumed draft restores both streams. Pre-OMON-119 drafts (without
    // this field) restore as `[]`, which is correct — no monitored
    // events were authored in those sessions.
    builderMonitoredEvents: state.builderMonitoredEvents.map((m) => ({ ...m })),
    planMonths: state.planMonths,
    planStartDate: state.planStartDate,
    // The pinned office contact is part of the draft, not of the live plan:
    // choosing it mid-edit and then refreshing must not silently lose the
    // choice. Shape rules (and why it is a plain id, not a reference) live in
    // ../loaders/plan-stewardship, which is unit-tested.
    builderCustodian: toDraftCustodian(state.builderCustodian),
  });
  // Step B5 (data-model-drift audit 2026-05-21): no _episodeOfCareId
  // denorm write. The plan→EoC relationship lives canonically on
  // _episodeofcare.carePlan[] (parent→child Reference array). The
  // post-create EoC.carePlan[] update lives in the addDoc branch below.
  // OMON-160: drafts MUST carry subject pointing at the EpisodeOfCare so
  // the Firestore Security Rules' userOnSubjectEocData traversal can
  // resolve (get(subject.reference) -> EoC -> userOnEoc/userManagesEocOrg).
  // Without it, the write rule's data-dependent branch denies because
  // resource.data.subject is null.
  const eocRef = doc(db, "_episodeofcare", eocId);
  const docPayload = {
    resourceType: "CarePlan",
    status: "draft",
    intent: "plan",
    title: `${state.selectedPatient.name} -- Outcome Monitoring (DRAFT)`,
    subject: { reference: eocRef },
    _draftState: draftStateJson,
    _draftLastSavedAt: new Date().toISOString(),
    _sourceApplication: "outcomemonitoring_web",
    // OMON-143: stamp meta.security from writer's dataScope claim on
    // every draft save (both create + updateDoc; the latter overwrites
    // the existing meta intentionally — re-stamps based on the current
    // session's claims, which is correct: the writer's scope determines
    // the doc's scope).
    meta: {
      source: "https://sweettreeconnect.com/fhir/ActorDefinition/outcomemonitoring_web",
      security: metaSecurityFor(state.authClaims),
    },
  };
  if (state.currentDraftId) {
    await updateDoc(doc(db, "_carePlan", state.currentDraftId), docPayload);
  } else {
    const ref = await addDoc(collection(db, "_carePlan"), {
      ...docPayload,
      created: new Date().toISOString(),
    });
    state.currentDraftId = ref.id;
    // Pattern A — add the draft to the parent EoC's canonical carePlan[]
    // Reference array. Per the data-model-drift audit
    // (architecture/audits/2026-05-21-omon-data-model-drift.md), parents
    // are the canonical source of truth for plan membership. Subsequent
    // autosaves (updateDoc on the same draft id) don't touch carePlan[]
    // again because the entry is already there.
    await updateDoc(doc(db, "_episodeofcare", eocId), {
      carePlan: arrayUnion({
        ___use: "The plans of care that apply to this episode (FHIR R5 EpisodeOfCare.carePlan)",
        reference: ref,
      }),
    });
    // OMON-351: the list is no longer re-read on every Plans-tab click, so
    // mark the draft pill here, where the draft comes into being. The row
    // object is the list's own (router.ts resolves it from the list).
    const listed = state.patients.find((p) => p.docId === eocId);
    if (listed) listed.hasDraft = true;
    if (state.selectedPatient?.docId === eocId) state.selectedPatient.hasDraft = true;
  }
}

// OMON-035: deserialise the draft blob into in-memory builder state.
// OMON-064 fix: the parsed JSON used to be assigned to a local variable
// named `state`, which shadowed the imported state module — every write
// inside silently went to the parsed object, not the app state, and
// Resume Draft was a silent no-op. Renamed local to `restored`.
export function restoreDraftState(draftStateJson: string) {
  try {
    const restored = JSON.parse(draftStateJson);
    state.builderGoals = (restored.builderGoals || []).map((o: any) => ({
      ...o,
      // OMON-183: plain boolean always (same normalisation as the ME path).
      mandatory: o.mandatory === true,
      // ADR-0035 am.8: the goal's role set survives a draft round trip.
      shiftRoles: normaliseRoles(o.shiftRoles),
      _overrides: new Set<number>(o._overrides || []),
      // OMON-135: drafts saved pre-OMON-135 lack this field; default to
      // 5 empty strings so the in-memory shape is always well-formed.
      // The planner can edit from there; at save time `carerGuidanceIsOverride`
      // will compare against canonical and decide whether to persist.
      carerGuidance: Array.isArray(o.carerGuidance) && o.carerGuidance.length === 5
        ? o.carerGuidance.map((s: any) => typeof s === "string" ? s : "")
        : ["", "", "", "", ""],
      // Ed 2026-09-29: the plan's one-text guidance override (null = catalogue's).
      guidanceOverride: typeof o.guidanceOverride === "string" ? o.guidanceOverride : null,
    }));
    // OMON-119 FULL: drafts saved pre-OMON-119 lack this field; restore
    // as empty array (correct — those sessions didn't author monitored
    // events).
    state.builderMonitoredEvents = (restored.builderMonitoredEvents || []).map((m: any) => ({
      odCode: String(m.odCode || ""),
      title: String(m.title || ""),
      subcategory: typeof m.subcategory === "string" ? m.subcategory : undefined,
      // OMON-174: drafts saved pre-OMON-174 lack the prompt; re-seed from the
      // canonical (matches what pick time would have done — the freeze only
      // starts once the entry lands on a saved plan version).
      prompt: typeof m.prompt === "string"
        ? m.prompt
        : (state.catalogueData.get(String(m.odCode || ""))?._monitoredEventPrompt || ""),
      // OMON-183: plain booleans always — drafts saved under the old
      // affordance-gating semantics carry undefined here, and an undefined
      // makes the default-chip claim a deviation that isn't one.
      mandatory: m.mandatory === true,
      shiftRoles: normaliseRoles(m.shiftRoles),
      // ADR-0035 am.9a: survives a draft round trip (absent in older drafts = off).
      allowNotApplicable: readAllowNotApplicable(m.allowNotApplicable),
      escalate: m.escalate === true,
      escalationThreshold: typeof m.escalationThreshold === "number" ? m.escalationThreshold : null,
      escalationDirection: m.escalationDirection === "below" ? "below" : "above",
      targetOn: m.targetOn === true || typeof m.targetValue === "number",
      targetValue: typeof m.targetValue === "number" ? m.targetValue : null,
    }));
    // Ed 2026-09-29: a draft copied from an older plan gets the minimum set on
    // resume (16 of 17 production drafts lacked the Shift log).
    state.builderMonitoredEvents = ensureMinimumSet(state.builderMonitoredEvents, state.catalogueData);
    if (typeof restored.planMonths === "number") state.planMonths = restored.planMonths;
    if (typeof restored.planStartDate === "string") state.planStartDate = restored.planStartDate;
    // Drafts saved before the office-contact picker lack this field and
    // restore as null — correct: nobody had made a choice in those sessions.
    state.builderCustodian = draftCustodianOf(restored.builderCustodian);
  } catch (err) {
    console.error("Failed to restore draft state:", err);
    state.builderGoals = [];
    state.builderMonitoredEvents = [];
  }
}

// OMON-035: explicit discard — delete the draft Firestore doc, clear state.
// Two entry points: (1) from inside the editor (state.currentDraftId is set), and
// (2) from the patient-detail Resume/Discard banner (state.currentDraftId is null
// because the user never entered the editor — state.patientDraft.docId is the
// authoritative ID in that case). Canonicalise from either source so the
// delete actually fires.
export async function discardDraft() {
  const draftDocId = state.currentDraftId || state.patientDraft?.docId || null;
  if (draftDocId) {
    try {
      await deleteDoc(doc(db, "_carePlan", draftDocId));
      // Pattern A — also remove the draft from the parent EoC's
      // canonical carePlan[] array so traversers don't see a dangling
      // Reference. arrayRemove matches by deep-equality so the exact
      // entry shape must mirror what saveDraft wrote.
      const eocId = state.selectedPatient?.docId;
      if (eocId) {
        try {
          await updateDoc(doc(db, "_episodeofcare", eocId), {
            carePlan: arrayRemove({
              ___use: "The plans of care that apply to this episode (FHIR R5 EpisodeOfCare.carePlan)",
              reference: doc(db, "_carePlan", draftDocId),
            }),
          });
        } catch (err) {
          console.error("Failed to remove draft from EoC.carePlan[]:", err);
        }
      }
    } catch (err) {
      console.error("Failed to delete draft doc:", err);
    }
  }
  state.currentDraftId = null;
  state.patientDraft = null;
  state.builderGoals = [];
  state.builderMode = "view";
  // Refresh BOTH the detail view and the patient list — the list's draft
  // pill needs to disappear for this patient, not just the banner.
  // OMON-351: one call does both — loadPatientPlan rewrites this client's
  // list row from the reads it makes. (The full list re-read that followed
  // here discarded its result, so it never refreshed the pill at all.)
  if (state.selectedPatient) await loadPatientPlan(state.selectedPatient);
  renderClientsView();
}

// OMON-035: 1-second debounced autosave. Fires on any input/change in the
// editor. Indicator updates with the timestamp of the LAST successful write.
export function scheduleAutosave() {
  // Gate: only autosave while a builder session is in flight. The picker
  // mode (#donePickingBtn workflow) IS in-flight — picking objectives
  // mutates state.builderGoals — so it must be included or Journey 1 loses
  // every change made via the picker (no input/change events fire there;
  // only clicks). Other input events on the page (patient-list search,
  // catalogue search, etc.) pass through silently because their state.builderMode
  // is "view" / "pick-catalogue" / etc.
  if (state.builderMode !== "edit" && state.builderMode !== "create" && state.builderMode !== "pick") return;
  if (!state.selectedPatient) return;
  if (state.autosaveDebounceTimer != null) clearTimeout(state.autosaveDebounceTimer);
  state.autosaveDebounceTimer = window.setTimeout(async () => {
    try {
      await saveDraft();
      const el = document.getElementById("__autosaveIndicator");
      if (el) {
        el.textContent = `Saved at ${new Date().toLocaleTimeString("en-GB", { hour12: false })}`;
        el.style.color = "";
      }
    } catch (err) {
      console.error("Autosave failed:", err);
      const el = document.getElementById("__autosaveIndicator");
      if (el) {
        el.textContent = "Autosave failed — retrying";
        el.style.color = "#991b1b";
      }
    }
  }, 1000);
}

// OMON-035: Discard confirmation — deletes the draft doc, no undo.
export function confirmAndDiscardDraft() {
  // If there's no draft persisted yet (user never edited), no need to confirm
  // — just exit the editor cleanly.
  if (!state.currentDraftId && !state.patientDraft) {
    state.builderMode = "view";
    state.builderGoals = [];
    renderClientsView();
    return;
  }
  const modal = document.createElement("div");
  modal.className = "modal-overlay";
  modal.id = "__discardConfirmModal";
  modal.innerHTML = `
    <div class="modal-box">
      <h3>Discard this draft?</h3>
      <p>The draft will be permanently deleted. Discarded drafts are not preserved as history.</p>
      <div class="modal-actions">
        <button class="btn-secondary" id="__cancelDiscard">Cancel</button>
        <button class="btn-amber" id="__confirmDiscard">Discard draft</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  const close = () => modal.remove();
  modal.querySelector("#__cancelDiscard")?.addEventListener("click", close);
  modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
  modal.querySelector("#__confirmDiscard")?.addEventListener("click", async () => {
    close();
    await discardDraft();
  });
}

// OMON-035: Save Live Version flow — confirmation modal + toast on success.
// The actual write/supersede happens in savePlan() (existing path).
export function confirmAndSaveLiveVersion() {
  // OMON-174 verify-loop fix: this OMON-035-era guard predated OMON-119 and
  // required developmental goals, silently swallowing the publish click for a
  // monitored-events-only plan. Mirror savePlan()'s own guard (and the save
  // button's disabled logic): either stream non-empty is publishable.
  if (!state.selectedPatient) return;
  const empty = isEmptyPlan(state.builderGoals.length, state.builderMonitoredEvents.length);
  if (!canPublish(empty, state.patientCarePlans.length > 0)) return;
  const draftVersion = state.patientCarePlans.length > 0
    ? (state.patientCarePlans[0]._version || 1) + 1
    : 1;
  const priorVersion = draftVersion - 1;
  const supersedesClause = priorVersion > 0
    ? `<p>Plan v${priorVersion} will be superseded and become a read-only historical record.</p>`
    : "";

  const modal = document.createElement("div");
  modal.className = "modal-overlay";
  modal.id = "__publishConfirmModal";
  modal.innerHTML = `
    <div class="modal-box">
      <h3>Publish this draft as v${draftVersion}?</h3>
      ${supersedesClause}
      ${empty
        ? `<p><strong>This version has nothing on it.</strong> Carers will have nothing to record for ${escapeHtml(state.selectedPatient.name)} until you add to the plan again.</p>`
        : `<p>Carers will see this plan immediately after you publish.</p>`}
      <div class="modal-actions">
        <button class="btn-secondary" id="__cancelPublish">Cancel</button>
        <button class="btn-amber" id="__confirmPublish">Publish v${draftVersion}</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  const close = () => modal.remove();
  modal.querySelector("#__cancelPublish")?.addEventListener("click", close);
  modal.addEventListener("click", (e) => { if (e.target === modal) close(); });
  modal.querySelector("#__confirmPublish")?.addEventListener("click", async () => {
    close();
    const draftIdAtPublish = state.currentDraftId;
    await savePlan();
    // OMON-035: after the new active CarePlan is written and prior actives
    // are superseded, delete the draft doc — its content is now the active.
    // savePlan() already triggered a re-render, but at that moment the draft
    // doc still existed in Firestore so loadPatientPlan picked it up and
    // rendered the "draft in progress" banner. Need a SECOND render after
    // the delete to flush that stale banner. Order is "active first, draft
    // delete second" so a failed delete doesn't leave the user with no
    // published plan AND no draft.
    if (draftIdAtPublish) {
      try {
        await deleteDoc(doc(db, "_carePlan", draftIdAtPublish));
        // Pattern A — remove the published-and-now-deleted draft from
        // the parent EoC's canonical carePlan[] array. savePlan()
        // already added the new active plan ref; this clears the stale
        // draft entry so traversers don't see a dangling Reference.
        const eocId = state.selectedPatient?.docId;
        if (eocId) {
          try {
            await updateDoc(doc(db, "_episodeofcare", eocId), {
              carePlan: arrayRemove({
                ___use: "The plans of care that apply to this episode (FHIR R5 EpisodeOfCare.carePlan)",
                reference: doc(db, "_carePlan", draftIdAtPublish),
              }),
            });
          } catch (err) {
            console.error("Failed to remove published draft from EoC.carePlan[]:", err);
          }
        }
      } catch (err) {
        console.error("Failed to delete draft after publish:", err);
      }
      state.currentDraftId = null;
      state.patientDraft = null;
      // OMON-351: also refreshes this client's list row (see discardDraft).
      if (state.selectedPatient) await loadPatientPlan(state.selectedPatient);
      renderClientsView();
    }
    if (priorVersion > 0) {
      showToast(`Plan v${priorVersion} superseded by v${draftVersion} — now live.`);
    } else {
      showToast(`Plan v${draftVersion} — now live.`);
    }
  });
}
